<template>
  <Dialog
    v-model:visible="dialogVisible"
    :header="idUserToDelete === 0 ? t('user.subUser.deleteMultiple.formTitle'): t('user.subUser.delete.formTitle')"
    :breakpoints="{ '960px': '75vw', '640px': '100vw' }"
    :style="{ width: '25vw' }"
    :content-style="{ textAlign: 'left' }"
    :modal="true"
    @hide="hideDialog"
  >
    <div class="pb-2">
      <label>
        <i
          class="far fa-info-circle"
          aria-hidden="true"
        />

        {{ idUserToDelete === 0 ? t('user.subUser.deleteMultiple.text'): t('user.subUser.delete.text') }}
      </label>
    </div>
    <template #footer>
      <Button
        :label="t('user.subUser.delete.rejectLabel')"
        icon="far fa-times"
        class="p-button p-button-secondary"
        @click="hideDialog"
      />
      <Button
        :label="t('user.subUser.delete.acceptLabel')"
        icon="far fa-lock"
        class="p-button p-button-primary"
        :loading="loadingInitiateAction"
        @click="initiateAction"
      />
    </template>
  </Dialog>

  <div class="sub-user-list">
    <PageHeader
      :title="t('user.subUser.title')"
      :summary="t('user.subUser.summary')"
    >
      <template #actions>
        <SecureSpmButton
          :label="t('user.subUser.create')"
          icon="fa fa-plus-square"
          class-style="p-button p-button-success"
          :on-authorized-click="handleCreateUser"
          required-feature="user.manage-sub-users"
        />
      </template>
    </PageHeader>

    <!-- Content Section -->
    <div class="page-content">
      <SpmTableAPI
        v-if="subUsers.length > 0"
        ref="tableRef"
        mode="client"
        :data="subUsers"
        :columns="columns"
        :show-reload-button="true"
        data-key="email"
        :row-actions-options="rowActions"
        :client-reload-fn="fetchSubUsers"
      >
        <!-- Composite column: fullname (firstname, lastname) -->
        <template #cell-fullname="{ data }">
          <div class="fullname-cell">
            <span
              v-if="formatFullName(data.first_name, data.last_name)"
              class="user-name"
            >
              {{ formatFullName(data.first_name, data.last_name) }}
            </span>
            <span
              v-else
              class="user-name user-name--empty"
            >
              {{ t('user.subUser.noName') }}
            </span>
          </div>
        </template>

        <!-- Single column: lang -->
        <template #cell-lang="{ data }">
          <div class="text-center w-full flex justify-content-center">
            <span
              v-if="cleanStr(data.lang)"
              v-tooltip.top="t(`languages.codes.${cleanStr(data.lang)}`)"
              class="lang-badge cursor-pointer"
            >{{ cleanStr(data.lang).toUpperCase() }}</span>
            <span
              v-else
              class="cell-empty"
            >-</span>
          </div>
        </template>

        <template #cell-expiry_date="{ data }">
          <div class="expiry-status">
            <!-- Aucune date d'expiration -->
            <div
              v-if="!data.expiry_date"
              class="flex align-items-center status-item"
            >
              <i
                class="fas fa-infinity status-icon mr-2 status-success"
              />
              <span class="status-success">{{ t('user.subUser.permissions.expiry.permanent') }}</span>
            </div>

            <!-- Date d'expiration valide (dans le futur) -->
            <div
              v-else-if="isExpiryDateValid(data.expiry_date)"
              class="flex align-items-center status-item"
            >
              <i
                class="fas fa-check-circle status-icon mr-2 status-success"
              />
              <span class="status-success">
                {{ t('user.subUser.permissions.expiry.validUntil', [formatExpiryDateOnly(data.expiry_date), formatExpiryTimeOnly(data.expiry_date)]) }}
              </span>
            </div>

            <!-- Date d'expiration expirée (dans le passé) -->
            <div
              v-else
              class="flex align-items-center status-item"
            >
              <i
                class="fas fa-times-circle status-icon mr-2 status-error"
              />
              <span class="status-error"> {{ t('user.subUser.permissions.expiry.expired') }}</span>
            </div>
          </div>
        </template>
      </SpmTableAPI>

      <!-- Empty state when no data -->
      <div
        v-if="subUsers.length === 0 && !isLoading"
        class="empty-state"
      >
        <i class="fas fa-users empty-icon" />
        <p>{{ t('user.subUser.noUsers') }}</p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import type {
  SpmColumnConfig,
  RowActionOption,
} from '@/components/table-rest-api/types';
import {
  UserTypeEnum,
  StatsType,
} from '@/types';
import { useI18n } from 'vue-i18n';
import { UserState } from '@/composables/User';
import {
  computed, defineComponent, onBeforeMount, ref,
} from 'vue';
import {
  UsersPagesSubscriptionsDeleteInput,
} from '@/types/generated-types/graphql';
import { DeleteUsersPermissions, getListOfShopSubUsers } from '@/composables/user/ShopsUsers';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Tooltip from 'primevue/tooltip';
import { showToastError, showToastSuccess } from '@/helpers';
import SecureSpmButton from '@/components/spm-primevue/SecureSpmButton.vue';
import {
  DeleteUsersPagesSubscriptions,
  getUserStatsPages,
  getUserStatsPagesV2,
} from '@/composables/Statistics/usersPagesSubscriptions';
// eslint-disable-next-line import/no-cycle
import router from '@/router';

export default defineComponent({
  name: 'SubUserList',

  components: {
    SpmTableAPI,
    PageHeader,
    Button,
    Dialog,
    SecureSpmButton,
  },

  directives: {
    tooltip: Tooltip,
  },

  setup() {
    const { t } = useI18n();
    const idShop = UserState?.activeShop?.id ?? 0;
    const dialogVisible = ref<boolean>(false);
    const idUserToDelete = ref<number | string>(0);
    const loadingInitiateAction = ref(false);
    const tableRef = ref();
    const subUsers = ref<Record<string, any>[]>([]);
    const isLoading = ref(false);
    const showDialog = async (idUser: number | string) => {
      idUserToDelete.value = idUser;
      dialogVisible.value = true;
    };

    const rowActions: RowActionOption[] = [
      {
        icon: 'fas fa-pencil-alt',
        label: t('user.subUser.modify.menuItem'),
        command: (data: any) => {
          router.push({
            name: 'user.manage-sub-users',
            params: { userId: data.id_user || data.email },
          });
        },

        visible: () => true,
      },
      {
        icon: 'fa-regular fa-trash-can',
        label: t('myLists.manage.bulkActions.delete.menuItem'),
        command: (data: any) => {
          dialogVisible.value = true;
          showDialog(data.id_user || data.email);
        },

        visible: () => true,
      },
    ];

    const columns = computed<SpmColumnConfig[]>(() => [
      {
        id: 'id_user',
        header: '',
        fields: [{
          name: 'id_user',
          label: '',
          sortable: false,
          filterable: false,
        }],
        hidden: true,
      },
      {
        id: 'id_shop',
        header: '',
        fields: [{
          name: 'id_shop',
          label: '',
          sortable: false,
          filterable: false,
        }],
        hidden: true,
      },
      // Composite: fullname with firstname and lastname
      {
        id: 'fullname',
        header: t('myLists.manage.customers.headers.fullName'),
        fields: [{
          name: 'first_name',
          label: t('myLists.manage.customers.headers.firstname'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'last_name',
          label: t('myLists.manage.customers.headers.lastname'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'email',
        header: t('myLists.manage.customers.headers.email'),
        fields: [{
          name: 'email',
          label: t('myLists.manage.customers.headers.email'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'lang',
        header: t('myLists.manage.customers.headers.lang'),
        fields: [{
          name: 'lang',
          label: t('myLists.manage.customers.headers.lang'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'expiry_date',
        header: t('user.subUser.permissions.expiry.column_header'),
        fields: [{
          name: 'expiry_date',
          label: t('user.subUser.permissions.expiry.column_header'),
          sortable: true,
          filterable: false,
        }],
      },
    ]);

    /**
     * Fetch data from API - used by clientReloadFn prop
     * Updates subUsers and returns the data
     */
    const fetchSubUsers = async () => {
      isLoading.value = true;
      try {
        const response = await getListOfShopSubUsers(UserState.user.idPsCustomer);
        if (response && response.data) {
          subUsers.value = response.data ?? [];
        } else {
          subUsers.value = [];
        }
      } catch (error) {
        console.error('Error fetching sub users:', error);
        subUsers.value = [];
      } finally {
        isLoading.value = false;
      }
      return subUsers.value;
    };

    /**
     * Reload table data using the exposed reloadData method
     */
    const reloadDataTable = async () => {
      if (tableRef.value) {
        await tableRef.value.reloadData();
      }
    };

    const deleteSpecificUser = async () => {
      loadingInitiateAction.value = true;

      // Vérifier que nous avons un ID numérique valide
      const userId = typeof idUserToDelete.value === 'number' ? idUserToDelete.value : null;
      if (!userId) {
        await showToastError(t('user.subUser.delete.deleteError'));
        loadingInitiateAction.value = false;
        dialogVisible.value = false;
        return;
      }

      const result = await DeleteUsersPermissions([{
        id_shops_user: userId,
        id_ps_customer: UserState.user.idPsCustomer,
      }]);

      if (result.err || !result.status) {
        const usersPagesSubscriptionsDelete: UsersPagesSubscriptionsDeleteInput[] = [];

        const dashboards = await getUserStatsPagesV2(userId, UserTypeEnum.USER, StatsType.DASHBOARD, UserState.activeShop?.idUser);

        if (dashboards && dashboards.success && dashboards.items.length) {
          dashboards.items.forEach((otherDashboard: any) => {
            usersPagesSubscriptionsDelete.push({
              id_users_pages_configuration: otherDashboard.id_users_pages_configuration,
              id_user: userId,
              user_type: UserTypeEnum.USER,
            });
          });
        }
        const stats = await getUserStatsPagesV2(userId, UserTypeEnum.USER, StatsType.STATS, UserState.activeShop?.idUser);

        if (stats && stats.success && stats.items.length) {
          stats.items.forEach((otherStatsPage: any) => {
            usersPagesSubscriptionsDelete.push({
              id_users_pages_configuration: otherStatsPage.id_users_pages_configuration,
              id_user: userId,
              user_type: UserTypeEnum.USER,
            });
          });
        }
        const emails = await getUserStatsPagesV2(userId, UserTypeEnum.USER, StatsType.EMAIL, UserState.activeShop?.idUser);

        if (emails && emails.success && emails.items.length) {
          emails.items.forEach((otherEmailPage: any) => {
            usersPagesSubscriptionsDelete.push({
              id_users_pages_configuration: otherEmailPage.id_users_pages_configuration,
              id_user: userId,
              user_type: UserTypeEnum.USER,
            });
          });
        }
        await DeleteUsersPagesSubscriptions(usersPagesSubscriptionsDelete);
        await showToastError(t('user.subUser.delete.deleteError'));
      } else {
        await showToastSuccess(t('user.subUser.delete.deleteSuccess'));
      }
      await reloadDataTable();

      dialogVisible.value = false;
      loadingInitiateAction.value = false;
    };

    const initiateAction = async () => {
      if (idUserToDelete.value !== 0) {
        await deleteSpecificUser();
        idUserToDelete.value = 0;
        return true;
      }
      return true;
    };

    const hideDialog = async () => {
      idUserToDelete.value = 0;
      dialogVisible.value = false;
    };

    const handleCreateUser = async () => {
      await router.push({
        name: 'user.manage-sub-users',
      });
    };

    const isExpiryDateValid = (expiryDate: string | null): boolean => {
      if (!expiryDate) return false;
      const date = new Date(expiryDate);
      const now = new Date();
      return date > now;
    };

    const formatExpiryDateOnly = (expiryDate: string): string => {
      const date = new Date(expiryDate);
      return date.toLocaleDateString();
    };

    const formatExpiryTimeOnly = (expiryDate: string): string => {
      const date = new Date(expiryDate);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // Helper to clean null/undefined strings
    const cleanStr = (str: string | null | undefined): string => {
      if (!str || str === 'null' || str === 'undefined') return '';
      return str;
    };

    // Helper to format full name
    const formatFullName = (firstName: string | null | undefined, lastName: string | null | undefined): string => {
      const first = cleanStr(firstName);
      const last = cleanStr(lastName);
      if (!first && !last) return '';
      return `${first} ${last}`.trim();
    };

    onBeforeMount(async () => {
      await fetchSubUsers();
    });

    return {
      t,
      columns,
      subUsers,
      isLoading,
      tableRef,
      dialogVisible,
      initiateAction,
      idUserToDelete,
      hideDialog,
      handleCreateUser,
      isExpiryDateValid,
      formatExpiryDateOnly,
      formatExpiryTimeOnly,
      loadingInitiateAction,
      formatFullName,
      cleanStr,
      rowActions,
      fetchSubUsers,
    };
  },
});
</script>

<style scoped>
/* Component Styles */
.expiry-status {
  display: flex;
  gap: 0.5rem;
}

.status-item {
  display: flex;
  align-items: center;
}

.status-icon {
  margin-right: 0.5rem;
}

.status-success {
  color: #10b981;
}

.status-error {
  color: #db5858;
}

.fullname-cell {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.user-name {
  font-weight: 500;
  color: #1f2937;
}

.user-name--empty {
  color: #9ca3af;
  font-style: italic;
}

.lang-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  font-size: 0.625rem;
  font-weight: 600;
  color: #4b5563;
  background: #f3f4f6;
  border-radius: 3px;
  letter-spacing: 0.025em;
}

.cell-empty {
  color: #d1d5db;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
  color: #6b7280;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #d1d5db;
}

.empty-state p {
  font-size: 1rem;
  margin: 0;
}
</style>
